import {FC, ReactNode, useState} from 'react'
import {FieldHelpLabel} from '../../../../../../../_metronic/helpers/components/FieldHelp'

type InvoiceSectionHeaderProps = {
  title: ReactNode
  description?: ReactNode
  helpKey?: string
  isRequired?: boolean
  eyebrow?: ReactNode
  actions?: ReactNode
}

const InvoiceSectionHeader: FC<InvoiceSectionHeaderProps> = ({
  title,
  description,
  helpKey,
  isRequired,
  eyebrow,
  actions,
}) => (
  <header className='mp-invoice-section-header'>
    <div className='mp-invoice-section-header__content'>
      {eyebrow && <span className='mp-invoice-section-header__eyebrow'>{eyebrow}</span>}
      <h3 className='mp-invoice-section-header__title'>
        {helpKey ? (
          <FieldHelpLabel label={title} helpKey={helpKey} isRequired={isRequired} />
        ) : (
          title
        )}
      </h3>
      {description && <p className='mp-invoice-section-header__description'>{description}</p>}
    </div>
    {actions && <div className='mp-invoice-section-header__actions'>{actions}</div>}
  </header>
)

export type InvoiceDetailItem = {
  label: ReactNode
  value: ReactNode
  ltr?: boolean
  wide?: boolean
}

const hasDetailValue = (value: ReactNode) =>
  value !== undefined && value !== null && value !== '' && value !== false

const InvoiceDetailsGrid: FC<{items: InvoiceDetailItem[]}> = ({items}) => {
  const visibleItems = items.filter((item) => hasDetailValue(item.value))
  if (visibleItems.length === 0) return null

  return (
    <dl className='mp-invoice-details-grid'>
      {visibleItems.map((item, index) => (
        <div
          className={`mp-invoice-details-grid__item${item.wide ? ' is-wide' : ''}`}
          key={`${String(item.label)}-${index}`}
        >
          <dt>{item.label}</dt>
          <dd className={item.ltr ? 'englishText' : undefined} dir={item.ltr ? 'ltr' : undefined}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export type InvoiceSummaryItem = {
  key: string
  label: ReactNode
  value: ReactNode
  unit?: ReactNode
  tone?: 'default' | 'success' | 'warning' | 'danger'
  emphasis?: boolean
}

type InvoiceFinancialSummaryProps = {
  title: ReactNode
  description?: ReactNode
  items: InvoiceSummaryItem[]
  warning?: ReactNode
  status?: ReactNode
}

const InvoiceFinancialSummary: FC<InvoiceFinancialSummaryProps> = ({
  title,
  description,
  items,
  warning,
  status,
}) => (
  <section className='mp-invoice-financial-summary' aria-label={String(title)}>
    <InvoiceSectionHeader title={title} description={description} actions={status} />
    <dl className='mp-invoice-financial-summary__rows'>
      {items.map((item) => (
        <div
          key={item.key}
          className={`mp-invoice-financial-summary__row${item.emphasis ? ' is-emphasis' : ''}${
            item.tone && item.tone !== 'default' ? ` is-${item.tone}` : ''
          }`}
        >
          <dt>{item.label}</dt>
          <dd>
            <b>{item.value}</b>
            {item.unit && <span>{item.unit}</span>}
          </dd>
        </div>
      ))}
    </dl>
    {warning && (
      <div className='mp-invoice-financial-summary__warning' role='alert'>
        <i className='las la-exclamation-triangle' aria-hidden='true'></i>
        <span>{warning}</span>
      </div>
    )}
  </section>
)

const InvoiceEmptyState: FC<{title: ReactNode; description?: ReactNode; icon?: string}> = ({
  title,
  description,
  icon = 'la-receipt',
}) => (
  <div className='mp-invoice-empty-state'>
    <i className={`las ${icon}`} aria-hidden='true'></i>
    <strong>{title}</strong>
    {description && <span>{description}</span>}
  </div>
)

const CopyableInvoiceValue: FC<{value: string; label: string}> = ({value, label}) => {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    if (!navigator.clipboard?.writeText) return
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // The full value remains visible when clipboard permissions are unavailable.
    }
  }

  return (
    <span className='mp-invoice-copyable-value'>
      <span className='mp-invoice-copyable-value__text englishText' dir='ltr' title={value}>
        {value}
      </span>
      <button type='button' onClick={copy} title={`کپی ${label}`} aria-label={`کپی ${label}`}>
        <i className={`las ${copied ? 'la-check' : 'la-copy'}`} aria-hidden='true'></i>
      </button>
    </span>
  )
}

export {
  CopyableInvoiceValue,
  InvoiceDetailsGrid,
  InvoiceEmptyState,
  InvoiceFinancialSummary,
  InvoiceSectionHeader,
}
